import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { format, parseISO } from 'date-fns';
import { useCallback, useEffect, useState } from 'react';
import { Accordion, Alert, Breadcrumb, Button, Card, Col, Container, Modal, Row, Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { Tasks } from '../../api/Tasks';
import { Therapies } from '../../api/Therapies';
import AppLoader from '../../components/AppLoader';
import { useToasts } from '../../contexts/toasts';
import { Task } from '../../models/Task';
import { pType } from '../../models/TaskParameter';

export default function TherapyDetail() {
    const { id } = useParams();
    const [therapy, setTherapy] = useState<any>();
    const { t } = useTranslation();
    const [showTaskModal, setShowTaskModal] = useState<boolean>(false);
    const [taskDetail, setTaskDetail] = useState<Task>()
    const [taskConfig, setTaskConfig] = useState<any>()
    const { showToast } = useToasts();
    useEffect(() => {
        if (id) {
            initTherapy()
        }

    }, [id]);

    const initTherapy = useCallback(() => {
        Therapies.getTherapyDetail(Number(id!))
            .then(
                ({ data: therapy }) => {
                    setTherapy(therapy)
                },
                (err) => {
                    // handle error
                }
            )
    }, []);

    const openTaskModal = (task: Task) => {
        if (!task.parameters?.length) {

            Tasks.getTenantTask(String(task.taskId!)).then(({ data: tsk }) => {
                setTaskDetail(tsk);
            }, (err) => {
                const message = err.response?.data?.message || err.message;
                const status = err.response?.status;
                showToast!('task-error', message, `Task Detail Error ${status ? `(${status})` : ''}`, 'danger');
            })

            Tasks.getTenantTaskInstanceConfig(task.id!).then(({ data: tskConf }) => {
                setTaskConfig(tskConf)
            }, (err) => {
                const message = err.response?.data?.message || err.message;
                const status = err.response?.status;
                showToast!('task-error', message, `Task Config Error ${status ? `(${status})` : ''}`, 'danger');
            })

        }

        setShowTaskModal(true);
    }

    const handleTaskModalClose = () => {
        setShowTaskModal(false);
        setTaskDetail(undefined);
    }

    const renderTherapy = () => {
        return (
            <>
                <Row className="mb-2">
                    <Col className="d-flex align-items-center">

                        <h6 className='mb-0'>{t('common.patient')}:</h6>
                        <span className='ms-2'>{therapy.patientName}</span>

                    </Col>
                    <Col className="d-flex align-items-center">

                        <h6 className='mb-0'>{t('common.scheduledDate')}:</h6>
                        <span className='ms-2'>{therapy.scheduledDate && format(parseISO(therapy.scheduledDate!), 'dd/MM/yyyy HH:mm')}</span>

                    </Col>
                    <Col className="d-flex align-items-center">

                        <h6 className='mb-0'>{t('common.executionDate')}:</h6>
                        <span className='ms-2'>{therapy.executionDate && format(parseISO(therapy.executionDate!), 'dd/MM/yyyy HH:mm')}</span>

                    </Col>
                </Row>
                <Row className="mb-2">
                    <Col md={4} className="d-flex align-items-center">

                        <h6 className='mb-0'>{t('common.status')}:</h6>
                        <strong className={`ms-2 ${therapy.isCompleted ? 'text-success' : 'text-warning'}`}>{therapy.isCompleted ? 'Completed' : 'Pending'}</strong>

                    </Col>
                    <Col md={4} className="d-flex align-items-center">

                        <h6 className='mb-0'>{t('common.error')}:</h6>
                        <strong className={`ms-2 ${!therapy.parsingSuccess || therapy.commitError ? 'text-danger' : 'text-success'}`} > {!therapy.parsingSuccess || therapy.commitError ? 'Yes' : 'No'} </strong>

                    </Col>

                </Row>

                {renderResults()}
            </>
        )
    }


    const renderResults = () => {

        if (!therapy.parsingSuccess) {
            return (
                <Row className="mt-3 mb-3">
                    <h6>Errors</h6>
                    <Col>
                        <Card>
                            {therapy.parsingErrorDetails.map((err: any) =>
                                <Card.Body className='border-bottom'>
                                    <Row >
                                        <Col>
                                            {err}
                                        </Col>
                                    </Row>
                                </Card.Body>
                            )}
                        </Card>
                    </Col>
                </Row>
            )
        }

        if (therapy.commitError) {

            return (
                <Row className="mt-3 mb-3">

                    <Col>
                        <Alert variant='danger'> Internal Server Error</Alert>
                    </Col>
                </Row>
            )
        }

        return (
            <Row className="mt-3 mb-3">
                <h6>Tasks</h6>
                <Col>
                    <Accordion defaultActiveKey="0">
                        {therapy.tasks?.map((tsk: any, index: string) =>
                            <Accordion.Item eventKey={String(index)} key={`task-${index}`}>
                                <Accordion.Header className='highlight'>{tsk.name}</Accordion.Header>
                                <Accordion.Body>
                                    <Button className="d-block me-0 ms-auto" onClick={() => openTaskModal(tsk)}>
                                        <FontAwesomeIcon icon={solid('eye')} />
                                        <span className='ms-2'>{t('common.viewConfiguration')}</span>
                                    </Button>
                                    {tsk.metrics && <Card.Body className='border-bottom'>
                                        <Row >
                                            <Col>
                                                <h6 className='mb-0'>Metric type</h6>
                                            </Col>
                                            <Col>
                                                <h6 className='mb-0'>Insert date</h6>
                                            </Col>
                                            <Col>
                                                <h6 className='mb-0'>Run</h6>
                                            </Col>
                                            <Col>
                                                <h6 className='mb-0'>Level</h6>
                                            </Col>
                                            <Col>
                                                <h6 className='mb-0'>Value</h6>
                                            </Col>
                                        </Row>
                                    </Card.Body>}
                                    {tsk.metrics?.map((metric: any, index: number) =>
                                        <Card.Body className='border-bottom' key={`metric-${index}`}>
                                            <Row >
                                                <Col>
                                                    {metric.metricType}
                                                </Col>
                                                <Col>
                                                    {metric.insertDate && format(parseISO(metric.insertDate), 'dd/MM/yyyy HH:mm')}
                                                </Col>
                                                <Col>
                                                    {metric.run}
                                                </Col>
                                                <Col>
                                                    {metric.level}
                                                </Col>
                                                <Col>
                                                    {metric.value}
                                                </Col>
                                            </Row>
                                        </Card.Body>
                                    )}
                                </Accordion.Body>
                            </Accordion.Item>)}
                    </Accordion>
                </Col>
            </Row >
        )
    }

    function customParamViewer(index: number, key: string) {

        switch (taskDetail?.parameters[index].type) {
            case pType.INTEGER:
            case pType.FLOAT:
            case pType.BOOLEAN:
            case pType.TEXT:
                return (
                    <td colSpan={4}>
                        <span> {taskConfig.config[key]} </span>
                    </td>
                )
            case pType.SEEDFORRANDOM:
                return (<>
                    <td>
                        <span> {taskConfig.config[key]?.s0} </span>
                    </td>
                    <td>
                        <span> {taskConfig.config[key]?.s1} </span>
                    </td>
                    <td>
                        <span> {taskConfig.config[key]?.s2} </span>
                    </td>
                    <td>
                        <span> {taskConfig.config[key]?.s3} </span>
                    </td>
                </>)
            case pType.GENERATEDQUESTIONS:
                return (
                    <td colSpan={4}>
                        <Row className='d-flex justify-content-between mb-1 align-items-center'>
                            <Col md={4}>
                                <p className="mb-0 fw-bold">Question</p>
                            </Col>
                            <Col md={4}>
                                <p className="mb-0 fw-bold">Answers</p>
                            </Col>
                            <Col md={4}>
                                <p className="mb-0 fw-bold">Correct</p>
                            </Col>
                        </Row >
                        {JSON.parse(taskConfig.config[key]).map((x: any, gQi: number) => {
                            return (
                                <Row className="mb-1">
                                    < Col md={4} >
                                        <span> {x.question}</span>
                                    </Col>
                                    <Col md={4}>
                                        <span> {x.answers.toString()} </span>
                                    </Col>
                                    <Col md={4}>
                                        <span> {x.correct.toString()} </span>
                                    </Col >
                                </Row >
                            )

                        })}
                    </td >
                )
            case pType.ENUM:
                return (<>
                    <td colSpan={4}>
                        <Row>
                            <Col md={8}>
                                <span>{taskConfig.config[key]}</span>
                            </Col>
                            <Col md={4} className='border-left'>
                                <span className='float-end'>{taskDetail?.parameters[index].description}</span>
                            </Col>
                        </Row>
                    </td>
                </>)
            case pType.LISTINTEGER:
            case pType.LISTFLOAT:
            case pType.LISTTEXT:
                return (<>
                    <td colSpan={4}>
                        <Row>
                            <Col>
                                <span>{taskConfig.config[key].toString()} </span>
                            </Col>
                        </Row>
                    </td>
                </>)
            default:
                return (<td></td>)
        }
    }

    const renderAsyncContent = () => {
        return therapy
            ? renderTherapy()
            : (
                <div className="d-flex py-3 justify-content-center">
                    <AppLoader size={150} />
                </div>
            )
    }

    return (
        <Container>
            <Breadcrumb>
                <Breadcrumb.Item linkAs={Link} linkProps={{ to: `/home` }}>
                    {t("pages.home") as string}
                </Breadcrumb.Item>
                <Breadcrumb.Item active>
                    {therapy?.protocolName + " - " + therapy?.sessionName}
                </Breadcrumb.Item>
            </Breadcrumb>
            <Card>
                <Card.Body>
                    <Card.Title></Card.Title>

                    {renderAsyncContent()}
                </Card.Body>
            </Card>

            <Modal show={showTaskModal} onHide={handleTaskModalClose} dialogClassName="modal-xxl" size='xl'>
                <Modal.Header closeButton>
                    <Modal.Title> {taskDetail?.name} </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Table bordered>
                        <thead>
                            <tr>
                                <th scope='col' colSpan={1} className='w-25'> Parametro </th>

                                <th scope='col' colSpan={4}> Valore</th>
                                <th scope='col' colSpan={1}> Unità</th>
                            </tr>
                        </thead>
                        <tbody>
                            {taskDetail && taskConfig &&  taskDetail?.parameters.map((param, i) => (
                                <tr key={i}>
                                    <td colSpan={1}>{param.key}</td>

                                    {customParamViewer(i, param.key)}
                                    <td colSpan={1}>{param.unit}</td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="light" className="border" size="sm" onClick={handleTaskModalClose}>{t('common.cancel')}</Button>
                </Modal.Footer>
            </Modal>
        </Container>
    )
}